import { useFormMutation } from '@/api/setters';
import Button from '@/components/ui/Button';
import { formMutationDto } from '@/dto/formMutationDto';
import { NextButton } from '@/features/v2/components/buttons/MoveForwardButton';
import { useStepper } from '@/features/v2/lib/stepper';
import { scrollToTop } from '@/helpers/scrollToTop';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export type BackButton = NextButton & {
  onBackButtonClick?: () => void;
  className?: string;
};

export const MoveBackButton = ({
  hidden,
  label,
  uniqTestId,
  withTrigger = false,
  onBackButtonClick,
  className,
}: BackButton) => {
  const { i18n } = useLingui();
  const {
    step,
    innerStep,
    prevStep,
    prevInnerStep,
    innerObject,
    forceDisableButton,
  } = useStepper();

  const methods = useFormContext();
  const { mutate } = useFormMutation();

  if (hidden) {
    return null;
  }

  return (
    <Button
      intent={'secondary'}
      type={'button'}
      className={cn('w-full md:w-auto', className)}
      data-testid={`back-button-${uniqTestId}`}
      onClick={async (e) => {
        // The step transition keeps this button mounted at the same tree
        // position, so the browser's :focus state (and its darker
        // focus:text-blue-800 style) would otherwise carry over onto the
        // next step's back button.
        e.currentTarget.blur();
        if (innerStep === 0) {
          prevStep();
          mutate({
            ...formMutationDto({
              form_data: methods.watch(),
              stepper: {
                step: step - 1,
                innerStep: { ...innerObject, [step]: innerStep },
              },
            }),
          });
          scrollToTop();
          return;
        }
        prevInnerStep();
        mutate({
          ...formMutationDto({
            form_data: methods.watch(),
            stepper: {
              step,
              innerStep: { ...innerObject, [step]: innerStep - 1 },
            },
          }),
        });
        scrollToTop();
        if (withTrigger) {
          await methods.trigger();
        }
        onBackButtonClick?.();
      }}
      disabled={forceDisableButton}
    >
      {label || i18n.t('Wróć')}
    </Button>
  );
};
